/* eslint-disable jsx-a11y/label-has-associated-control */
import { ChevronDown, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import {
  useCreateTaskMutation,
  useGetMattersQuery,
  useGetTaskGroupsQuery,
  useGetTaskStatusesQuery,
  useGetTaskTypesQuery,
} from '@/services/api';
import { TaskGroup, TaskType } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { createConsistentDate } from '@/utils/date';

import { Combobox } from './CreateTaskModal';

interface CreateImmediateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateImmediateTaskModal: React.FC<
  CreateImmediateTaskModalProps
> = ({ isOpen, onClose }) => {
  const { matter_id: currentMatterIdParam } = useParams();
  const [createTask] = useCreateTaskMutation();

  // Form state
  const [taskName, setTaskName] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedType, setSelectedType] = useState<TaskType | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<TaskGroup | null>(null);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(
    currentMatterIdParam || null,
  );

  // Fetch reference data
  const { data: mattersData } = useGetMattersQuery();
  const matters = mattersData?.results ?? [];
  const { data: statuses = [] } = useGetTaskStatusesQuery();
  const { data: types = [] } = useGetTaskTypesQuery();
  const { data: groups = [] } = useGetTaskGroupsQuery();

  // Resolved IDs we need
  const pendingStatus = statuses.find(
    (s) => s.canonical_status === CanonicalTaskStatus.PENDING,
  );
  const otherType = types.find(
    (t) => t.canonical_type === CanonicalTaskType.OTHER,
  );
  const miscGroup = groups.find(
    (g) => g.canonical_group === CanonicalTaskGroup.MISCELLANEOUS,
  );

  // Reset when modal closed/opened
  useEffect(() => {
    if (!isOpen) {
      setTaskName('');
      setSelectedMatterId(currentMatterIdParam || null);
      setSelectedType(null);
      setSelectedGroup(null);
    }
  }, [isOpen, currentMatterIdParam]);

  // Set default type/group once fetched
  useEffect(() => {
    if (!selectedType && otherType) {
      setSelectedType(otherType as unknown as TaskType);
    }
    if (!selectedGroup && miscGroup) {
      setSelectedGroup(miscGroup as unknown as TaskGroup);
    }
  }, [otherType, miscGroup, selectedType, selectedGroup]);

  const submitDisabled =
    !taskName ||
    !selectedMatterId ||
    !selectedType ||
    !selectedGroup ||
    !pendingStatus;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabled) return;

    const today = createConsistentDate(new Date());
    try {
      await createTask({
        matterId: selectedMatterId!,
        task: {
          name: taskName,
          group_id: selectedGroup!.id,
          type_id: selectedType!.id,
          status_id: pendingStatus!.id,
          order: -100,
          due_date: today,
          start_date: today,
        },
      }).unwrap();

      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create immediate task', error);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="md">
      <DialogTitle className="flex items-center gap-2">
        <Zap className="h-5 w-5 text-yellow-500" />
        Create Immediate Task
      </DialogTitle>

      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Matter Selection */}
          <Field className="flex flex-col gap-2">
            <Label>Matter</Label>
            <Combobox
              label=""
              placeholder="Select matter"
              options={matters.map((m) => ({ id: m.id, name: m.name }))}
              value={
                selectedMatterId
                  ? { id: selectedMatterId, name: selectedMatterId }
                  : null
              }
              onChange={(value) => setSelectedMatterId(value?.id || null)}
            />
          </Field>

          {/* Task Name */}
          <Field>
            <Label>Task Name</Label>
            <Input
              placeholder="Enter task name"
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              required
            />
          </Field>

          {/* Advanced options toggle */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Advanced Options
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
              />
            </button>
          </div>

          {showAdvanced && (
            <div className="space-y-6">
              {/* Task Type */}
              <Field className="flex flex-col gap-2">
                <Label>Task Type</Label>
                <Combobox
                  label=""
                  placeholder="Select task type"
                  options={types}
                  value={selectedType}
                  onChange={(value) => {
                    if (value) {
                      setSelectedType(value);
                      if (value.canonical_type !== CanonicalTaskType.OTHER) {
                        // Auto select associated group if provided
                        const associated = groups.find(
                          (g) => g.canonical_group === value.canonical_group,
                        );
                        if (associated) {
                          setSelectedGroup(associated as TaskGroup);
                        }
                      }
                    }
                  }}
                />
              </Field>

              {/* Task Group - shown if type is OTHER */}
              {selectedType?.canonical_type === CanonicalTaskType.OTHER && (
                <Field className="flex flex-col gap-2">
                  <Label>Task Group</Label>
                  <Combobox
                    label=""
                    placeholder="Select task group"
                    options={groups}
                    value={selectedGroup}
                    onChange={(value) =>
                      value && setSelectedGroup(value as TaskGroup)
                    }
                  />
                </Field>
              )}
            </div>
          )}
        </form>
      </DialogBody>

      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitDisabled}
          color="dark/zinc"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
